import { useQuery } from '@tanstack/react-query';
import { endpointsApi, endpointTestsApi, generatedUiApi } from '@/services/api';
import { flattenVersions } from '@/lib/generatedUi';

/**
 * Per-row metadata for the shared services table (task 378, Mockup J).
 *
 * Each listed service fetches three small facts, one react-query query per
 * source per row:
 *   - requestsTotal   — GET /endpoints/{id}/requests?limit=1 → pagination.total
 *   - interfaceSaved  — GET /generated-endpoint-uis?endpointId=… → any saved UI
 *   - savedTestsTotal — GET /endpoint-tests?endpointId=…&limit=1 → pagination.total
 *
 * Every value degrades to `undefined` while loading or on error, so the table
 * can render an em dash instead of a wrong number.
 */

/** Cache row metadata for a minute — dashboard glances, not live telemetry. */
const ROW_META_STALE_TIME_MS = 60_000;

export interface ServiceRowMeta {
  /** All-time request count for the endpoint; undefined while loading / on error. */
  requestsTotal: number | undefined;
  /** True when at least one generated UI is saved for the endpoint. */
  interfaceSaved: boolean | undefined;
  /** Saved endpoint-test count; undefined while loading / on error. */
  savedTestsTotal: number | undefined;
}

export function useServiceRowMeta(endpointId: string | undefined): ServiceRowMeta {
  const { data: requestsTotal } = useQuery({
    queryKey: ['service-row-requests-total', endpointId],
    queryFn: () =>
      endpointsApi.getRequests(endpointId!, 1, 1).then((res) => res.data.pagination.total),
    enabled: !!endpointId,
    staleTime: ROW_META_STALE_TIME_MS,
  });

  const { data: interfaceSaved } = useQuery({
    queryKey: ['service-row-interface-saved', endpointId],
    // Saved means "has a version to preview", not merely "has a parent UI row".
    queryFn: () =>
      generatedUiApi.list(endpointId!).then((res) => flattenVersions(res.data.data).length > 0),
    enabled: !!endpointId,
    staleTime: ROW_META_STALE_TIME_MS,
  });

  const { data: savedTestsTotal } = useQuery({
    queryKey: ['service-row-tests-total', endpointId],
    queryFn: () =>
      endpointTestsApi.list(endpointId!, 1, 1).then((res) => res.data.pagination.total),
    enabled: !!endpointId,
    staleTime: ROW_META_STALE_TIME_MS,
  });

  return { requestsTotal, interfaceSaved, savedTestsTotal };
}
